import React from 'react';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import {
  RECERT_CERT_TYPE,
  GROSS_RENT_CERT_TYPE,
  MOVE_IN_CERT_TYPE,
  INITIAL_CERT_TYPE,
  OTHER_HUD,
  OTHER,
  INTERIM_CERT_TYPE,
  RD_GROSS_RENT_CERT_TYPE,
  OTHER_PH,
} from '../../constants/affordableCertificationTypes';
import generalMessages from '../App/messages';
import componentMessages from './messages';
import { isRecertLate } from './utilsV2';

const messages = { ...generalMessages, ...componentMessages };

export const COUNT_NAME = <FormattedMessage {...messages.certifications} />;

const MULTI_HEADERS = {
  certificationNotes: {
    id: 'certificationNotes',
    label: <FormattedMessage {...messages.certificationNotes} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  complianceDocument: {
    id: 'complianceDocument',
    label: <FormattedMessage {...messages.complianceDocument} />,
    sortable: true,
    searchable: true,
    multiselect: true,
    dataType: 'string',
  },
  unit: {
    id: 'unit',
    label: <FormattedMessage {...messages.unit} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  occupiedOnNTV: {
    id: 'occupiedOnNTV',
    label: <FormattedMessage {...messages.noticeToVacate} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: [{ text: 'Yes', value: 'yes' }],
    dataType: 'string',
  },
};

const OTHER_HEADERS = [
  {
    id: 'status',
    label: <FormattedMessage {...messages.status} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    dataType: 'string',
  },
  {
    id: 'certificationType',
    label: <FormattedMessage {...messages.certificationType} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    dataType: 'string',
  },
  MULTI_HEADERS.unit,
  {
    id: 'floorPlan',
    label: <FormattedMessage {...messages.floorPlan} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'headOfHousehold',
    label: <FormattedMessage {...messages.headOfHousehold} />,
    sortable: true,
    searchable: true,
    dataType: 'string',
  },
  {
    id: 'effectiveDate',
    label: <FormattedMessage {...messages.effectiveDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'lastCertifiedDate',
    label: <FormattedMessage {...messages.lastCertifiedDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'nextRecertDueDate',
    label: <FormattedMessage {...messages.nextRecertDueDate} />,
    sortable: true,
    searchable: true,
    dataType: 'date',
  },
  {
    id: 'isPriorResident',
    label: <FormattedMessage {...messages.priorResident} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: [
      { text: 'Yes', value: 'yes' },
      { text: 'No', value: 'no' },
    ],
    dataType: 'string',
  },
  {
    id: 'isCorrection',
    label: <FormattedMessage {...messages.correction} />,
    sortable: true,
    searchable: false,
    multiselect: true,
    filterOptions: [
      { text: 'Yes', value: 'yes' },
      { text: 'No', value: 'no' },
    ],
    dataType: 'string',
  },
  {
    id: 'balance',
    label: <FormattedMessage {...messages.balance} />,
    sortable: true,
    searchable: false,
    dataType: 'number',
  },
  {
    id: 'latestComplianceDocument',
    label: <FormattedMessage {...messages.complianceDocument} />,
    sortable: true,
    searchable: false,
    dataType: 'string',
  },
  {
    id: 'daysSinceLastUpload',
    label: <FormattedMessage {...messages.daysSinceLastUpload} />,
    sortable: true,
    searchable: false,
    dataType: 'number',
  },
  MULTI_HEADERS.certificationNotes,
];

export const RECERT_DUE_FILTER_VALUES = {
  LATE: 'late',
  UNDER_30: 'under30',
  BETWEEN_31_AND_60: 'between31and60',
  BETWEEN_61_AND_90: 'between61and90',
  BETWEEN_90_AND_120: 'between90and120',
  UPCOMING: 'upcoming',
};

// ENG-30054: ordered bucket ids (Past-Due-to-120+ order) for the Recerts
// tab's new interactive `PlanningRangeFilter` cards. These are the exact
// same bucket ids as `RECERT_DUE_FILTER_VALUES` above (and the existing
// "Recert Due" column filter dropdown) — reused, not reintroduced, so
// selecting a card wires directly into the existing `recertDue` filter
// state/machinery below.
export const PLANNING_RANGE_BUCKETS = [
  RECERT_DUE_FILTER_VALUES.LATE,
  RECERT_DUE_FILTER_VALUES.UNDER_30,
  RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60,
  RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90,
  RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120,
  RECERT_DUE_FILTER_VALUES.UPCOMING,
];

// Maps backend recertRange values to frontend filter values
export const RECERT_RANGE_TO_FILTER_MAP = {
  lateRecerts: RECERT_DUE_FILTER_VALUES.LATE,
  under30: RECERT_DUE_FILTER_VALUES.UNDER_30,
  between31And60: RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60,
  between61And90: RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90,
  between90And120: RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120,
  upcoming: RECERT_DUE_FILTER_VALUES.UPCOMING,
};

const RECERT_DUE_DATE_INPUT_FORMATS = [
  'MM/DD/YYYY',
  'YYYY-MM-DD',
  moment.ISO_8601,
];

const getDaysUntilRecertDue = (filterValue) => {
  const today = moment().startOf('day');
  const recertDueMoment = moment(
    filterValue,
    RECERT_DUE_DATE_INPUT_FORMATS,
    true,
  );

  if (!recertDueMoment.isValid()) {
    return null;
  }

  return recertDueMoment.startOf('day').diff(today, 'days');
};

const recertDueDateFilterFn = (filterBy, filterValue) => {
  console.log('filterValue:', filterValue);
  let shouldIncludeArray = [];
  const daysUntilDue = getDaysUntilRecertDue(filterValue);

  if (filterBy.length === 0) {
    shouldIncludeArray.push(true);
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.LATE)) {
    shouldIncludeArray.push(isRecertLate({ recertDue: filterValue }));
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.UNDER_30)) {
    shouldIncludeArray.push(
      daysUntilDue != null && daysUntilDue >= 0 && daysUntilDue <= 30,
    );
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60)) {
    shouldIncludeArray.push(
      daysUntilDue != null && daysUntilDue >= 31 && daysUntilDue <= 60,
    );
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90)) {
    shouldIncludeArray.push(
      daysUntilDue != null && daysUntilDue >= 61 && daysUntilDue <= 90,
    );
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120)) {
    shouldIncludeArray.push(
      daysUntilDue != null && daysUntilDue >= 91 && daysUntilDue <= 120,
    );
  }
  if (filterBy.includes(RECERT_DUE_FILTER_VALUES.UPCOMING)) {
    shouldIncludeArray.push(daysUntilDue != null && daysUntilDue > 120);
  }

  return shouldIncludeArray.includes(true);
};

const recertDueRangeFilterFn = (filterBy, filterValue) => {
  if (filterBy.length === 0) {
    return true;
  }

  const mappedFilterValue = RECERT_RANGE_TO_FILTER_MAP[filterValue] ?? null;
  return mappedFilterValue ? filterBy.includes(mappedFilterValue) : false;
};

/**
 * Returns the recertDue property path map entry.
 * Filtering uses the backend-computed recertRange as the source of truth.
 */
export const getRecertDuePropertyPathEntry = () => ({
  path: 'recertDue',
  dataType: 'date',
  filterPath: 'recertRange',
  filterFn: recertDueRangeFilterFn,
});

export const HEADERS = {
  [RECERT_CERT_TYPE]: [
    {
      id: 'status',
      label: <FormattedMessage {...messages.status} />,
      sortable: true,
      searchable: true,
      multiselect: true,
      dataType: 'string',
    },
    {
      id: 'programName',
      label: <FormattedMessage {...messages.programName} />,
      sortable: true,
      searchable: true,
      multiselect: true,
      dataType: 'string',
    },
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'moveInDate',
      label: <FormattedMessage {...messages.moveIn} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'leaseStartDate',
      label: <FormattedMessage {...messages.leaseStartDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'lastCertified',
      label: <FormattedMessage {...messages.lastCertified} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'recertDue',
      label: <FormattedMessage {...messages.recertDue} />,
      sortable: true,
      searchable: true,
      filterOptions: [
        {
          text: <FormattedMessage {...messages.late} />,
          value: RECERT_DUE_FILTER_VALUES.LATE,
        },
        {
          text: <FormattedMessage {...messages.under30} />,
          value: RECERT_DUE_FILTER_VALUES.UNDER_30,
        },
        {
          text: <FormattedMessage {...messages.between31And60} />,
          value: RECERT_DUE_FILTER_VALUES.BETWEEN_31_AND_60,
        },
        {
          text: <FormattedMessage {...messages.between61And90} />,
          value: RECERT_DUE_FILTER_VALUES.BETWEEN_61_AND_90,
        },
        {
          text: <FormattedMessage {...messages.between90And120} />,
          value: RECERT_DUE_FILTER_VALUES.BETWEEN_90_AND_120,
        },
        {
          text: <FormattedMessage {...messages.upcoming} />,
          value: RECERT_DUE_FILTER_VALUES.UPCOMING,
        },
      ],
      multiselect: true,
      dataType: 'date',
    },
    MULTI_HEADERS.occupiedOnNTV,
    {
      id: 'doNotRenew',
      label: <FormattedMessage {...messages.doNotRenew} />,
      sortable: true,
      searchable: false,
      dataType: 'boolean',
    },
    {
      id: 'underEviction',
      label: <FormattedMessage {...messages.underEviction} />,
      sortable: true,
      searchable: false,
      dataType: 'boolean',
    },
    {
      id: 'correction',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'balance',
      label: <FormattedMessage {...messages.balance} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'notice120',
      label: <FormattedMessage {...messages.notice120} />,
      sortable: true,
      searchable: false,
      filterOptions: [{ text: 'Yes', value: 'yes' }],
      dataType: 'string',
    },
    {
      id: 'notice90',
      label: <FormattedMessage {...messages.notice90} />,
      sortable: true,
      searchable: false,
      filterOptions: [{ text: 'Yes', value: 'yes' }],
      dataType: 'string',
    },
    {
      id: 'notice60',
      label: <FormattedMessage {...messages.notice60} />,
      sortable: true,
      searchable: false,
      filterOptions: [{ text: 'Yes', value: 'yes' }],
      dataType: 'string',
    },
    {
      id: 'notice30',
      label: <FormattedMessage {...messages.notice30} />,
      sortable: true,
      searchable: false,
      filterOptions: [{ text: 'Yes', value: 'yes' }],
      dataType: 'string',
    },
    MULTI_HEADERS.complianceDocument,
    MULTI_HEADERS.certificationNotes,
  ],
  [GROSS_RENT_CERT_TYPE]: [
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'newGrossRent',
      label: <FormattedMessage {...messages.newGrossRent} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'newHUDUA',
      label: <FormattedMessage {...messages.newHUDUA} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'grEffectiveDate',
      label: <FormattedMessage {...messages.grEffectiveDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'anticipatedVoucherDate',
      label: <FormattedMessage {...messages.anticipatedVoucherDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'priorCertificationDate',
      label: <FormattedMessage {...messages.priorCertificationDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'otherCertInProgress',
      label: <FormattedMessage {...messages.otherCertInProgress} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    { ...MULTI_HEADERS.occupiedOnNTV, filterOptions: undefined },
    {
      id: 'newSubsidy',
      label: <FormattedMessage {...messages.newSubsidy} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'newResidentRent',
      label: <FormattedMessage {...messages.newResidentRent} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'newUtilityReimbursement',
      label: <FormattedMessage {...messages.newUtilityReimbursement} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'residentSignatureRequired',
      label: <FormattedMessage {...messages.residentSignatureRequired} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'correction',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'generated59a',
      label: <FormattedMessage {...messages.generated59a} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'uploadedDocument',
      label: <FormattedMessage {...messages.uploadedDocument} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'generate59a',
      label: <FormattedMessage {...messages.generate59a} />,
      sortable: false,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'action',
      label: <FormattedMessage {...messages.action} />,
      sortable: false,
      searchable: false,
      dataType: 'string',
    },
  ],
  [RD_GROSS_RENT_CERT_TYPE]: [
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'newNoteRent',
      label: <FormattedMessage {...messages.newNoteRent} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'newBasicRent',
      label: <FormattedMessage {...messages.newBasicRent} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'newRDUA',
      label: <FormattedMessage {...messages.newRDUa} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },

    {
      id: 'grEffectiveDate',
      label: <FormattedMessage {...messages.grEffectiveDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'priorCertificationDate',
      label: <FormattedMessage {...messages.priorCertificationDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'otherCertInProgress',
      label: <FormattedMessage {...messages.otherCertInProgress} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    { ...MULTI_HEADERS.occupiedOnNTV, filterOptions: undefined },
    {
      id: 'newSubsidy',
      label: <FormattedMessage {...messages.newSubsidy} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'newResidentRent',
      label: <FormattedMessage {...messages.newResidentRent} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'newUtilityReimbursement',
      label: <FormattedMessage {...messages.newUtilityReimbursement} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
      sx: { backgroundColor: 'primary.dark' },
    },
    {
      id: 'residentSignatureRequired',
      label: <FormattedMessage {...messages.residentSignatureRequired} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'correction',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },

    {
      id: 'uploadedDocument',
      label: <FormattedMessage {...messages.uploadedDocument} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'generateRDGrossRent',
      label: <FormattedMessage {...messages.generateRDGrossRent} />,
      sortable: false,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'action',
      label: <FormattedMessage {...messages.action} />,
      sortable: false,
      searchable: false,
      dataType: 'string',
    },
  ],
  [MOVE_IN_CERT_TYPE]: [
    {
      id: 'status',
      label: <FormattedMessage {...messages.status} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    {
      id: 'programName',
      label: <FormattedMessage {...messages.programName} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'moveInDate',
      label: <FormattedMessage {...messages.scheduledMoveInDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'leaseStartDate',
      label: <FormattedMessage {...messages.leaseStartDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'isResident',
      label: <FormattedMessage {...messages.currentResident} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'balance',
      label: <FormattedMessage {...messages.balance} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    {
      id: 'isCorrection',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    MULTI_HEADERS.complianceDocument,
    {
      id: 'daysSinceLastUpload',
      label: <FormattedMessage {...messages.daysSinceLastUpload} />,
      sortable: true,
      searchable: true,
      dataType: 'number',
    },
    MULTI_HEADERS.certificationNotes,
  ],
  [INITIAL_CERT_TYPE]: [
    {
      id: 'status',
      label: <FormattedMessage {...messages.certStatus} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    {
      id: 'programName',
      label: <FormattedMessage {...messages.programName} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'effectiveDate',
      label: <FormattedMessage {...messages.effectiveDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'leaseStartDate',
      label: <FormattedMessage {...messages.leaseStartDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'isPriorResident',
      label: <FormattedMessage {...messages.priorResident} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'balance',
      label: <FormattedMessage {...messages.balance} />,
      sortable: true,
      searchable: false,
      dataType: 'number',
    },
    {
      id: 'isCorrection',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'latestComplianceDocument',
      label: <FormattedMessage {...messages.complianceDocument} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'daysSinceLastUpload',
      label: <FormattedMessage {...messages.daysSinceLastUpload} />,
      sortable: true,
      searchable: false,
      dataType: 'number',
    },
    MULTI_HEADERS.certificationNotes,
  ],
  [INTERIM_CERT_TYPE]: [
    {
      id: 'status',
      label: <FormattedMessage {...messages.status} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    {
      id: 'programName',
      label: <FormattedMessage {...messages.programName} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      dataType: 'string',
    },
    MULTI_HEADERS.unit,
    {
      id: 'floorPlan',
      label: <FormattedMessage {...messages.floorPlan} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'headOfHousehold',
      label: <FormattedMessage {...messages.headOfHousehold} />,
      sortable: true,
      searchable: true,
      dataType: 'string',
    },
    {
      id: 'effectiveDate',
      label: <FormattedMessage {...messages.effectiveDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'lastCertifiedDate',
      label: <FormattedMessage {...messages.lastCertifiedDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'nextRecertDueDate',
      label: <FormattedMessage {...messages.nextRecertDueDate} />,
      sortable: true,
      searchable: true,
      dataType: 'date',
    },
    {
      id: 'isPriorResident',
      label: <FormattedMessage {...messages.priorResident} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'isCorrection',
      label: <FormattedMessage {...messages.correction} />,
      sortable: true,
      searchable: false,
      multiselect: true,
      filterOptions: [
        { text: 'Yes', value: 'yes' },
        { text: 'No', value: 'no' },
      ],
      dataType: 'string',
    },
    {
      id: 'balance',
      label: <FormattedMessage {...messages.balance} />,
      sortable: true,
      searchable: false,
      dataType: 'number',
    },
    {
      id: 'latestComplianceDocument',
      label: <FormattedMessage {...messages.complianceDocument} />,
      sortable: true,
      searchable: false,
      dataType: 'string',
    },
    {
      id: 'daysSinceLastUpload',
      label: <FormattedMessage {...messages.daysSinceLastUpload} />,
      sortable: true,
      searchable: false,
      dataType: 'number',
    },
    MULTI_HEADERS.certificationNotes,
  ],
  [OTHER_HUD]: OTHER_HEADERS,
  [OTHER_PH]: OTHER_HEADERS,
  [OTHER]: OTHER_HEADERS,
};

const OTHER_PROPERTY_PATH_MAP = {
  status: {
    path: 'displayStatus',
    dataType: 'string',
  },
  certificationType: {
    path: 'certificationType',
    dataType: 'string',
  },
  unit: {
    path: 'unit',
    dataType: 'string',
  },
  floorPlan: {
    path: 'floorplan',
    dataType: 'string',
  },
  headOfHousehold: {
    path: 'headOfHousehold',
    dataType: 'string',
  },
  effectiveDate: {
    path: 'voucherEffectiveDate',
    dataType: 'date',
  },
  lastCertifiedDate: {
    path: 'lastCertifiedDate',
    dataType: 'date',
  },
  nextRecertDueDate: {
    path: 'nextRecertDueDate',
    dataType: 'date',
  },
  isPriorResident: {
    path: 'isPriorResident',
    dataType: 'string',
  },
  isCorrection: {
    path: 'isCorrection',
    dataType: 'string',
  },
  balance: {
    path: 'balance',
    dataType: 'number',
  },
  latestComplianceDocument: {
    path: 'latestDocument.documentType.name',
    dataType: 'string',
  },
  daysSinceLastUpload: {
    path: 'daysSinceLastUpload',
    dataType: 'number',
  },
  certificationNotes: {
    path: 'certificationNotes',
    dataType: 'string',
  },
};
export const PROPERTY_PATH_MAP = {
  [RECERT_CERT_TYPE]: {
    status: {
      path: 'displayStatus',
      dataType: 'string',
    },
    programName: {
      path: 'programName',
      dataType: 'string',
    },
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorPlan',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    moveInDate: {
      path: 'moveInDate',
      dataType: 'date',
    },
    leaseStartDate: {
      path: 'leaseStartDate',
      dataType: 'date',
    },
    lastCertified: {
      path: 'lastCertified',
      dataType: 'date',
    },
    recertDue: {
      path: 'recertDue',
      dataType: 'date',
      filterFn: recertDueDateFilterFn,
    },
    occupiedOnNTV: {
      path: 'occupiedOnNTV',
      dataType: 'string',
    },
    doNotRenew: {
      path: 'doNotRenew',
      dataType: 'boolean',
    },
    underEviction: {
      path: 'underEviction',
      dataType: 'boolean',
    },
    correction: {
      path: 'isCorrection',
      dataType: 'string',
    },
    balance: {
      path: 'balance',
      dataType: 'number',
    },
    notice120: {
      path: 'notice120',
      dataType: 'string',
    },
    notice90: {
      path: 'notice90',
      dataType: 'string',
    },
    notice60: {
      path: 'notice60',
      dataType: 'string',
    },
    notice30: {
      path: 'notice30',
      dataType: 'string',
    },
    complianceDocument: {
      path: 'complianceDocument',
      dataType: 'string',
    },
    certificationNotes: {
      path: 'certificationNotes',
      dataType: 'string',
    },
  },
  [RD_GROSS_RENT_CERT_TYPE]: {
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorPlanName',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    newNoteRent: {
      path: 'newNoteRent',
      dataType: 'number',
    },
    newBasicRent: {
      path: 'newBasicRent',
      dataType: 'number',
    },
    newRDUA: {
      path: 'newHUDUA',
      dataType: 'number',
    },
    grEffectiveDate: {
      path: 'grEffectiveDate',
      dataType: 'date',
    },
    priorCertificationDate: {
      path: 'priorCertificationDate',
      dataType: 'date',
    },
    otherCertInProgress: {
      path: 'otherCertInProgress',
      dataType: 'string',
    },
    occupiedOnNTV: {
      path: 'occupiedOnNTV',
      dataType: 'string',
    },
    newSubsidy: {
      path: 'newSubsidy',
      dataType: 'number',
    },
    newResidentRent: {
      path: 'newResidentRent',
      dataType: 'number',
    },
    newUtilityReimbursement: {
      path: 'newUtilityReimbursement',
      dataType: 'number',
    },
    residentSignatureRequired: {
      path: 'residentSignatureRequired',
      dataType: 'string',
    },
    correction: {
      path: 'isCorrection',
      dataType: 'string',
    },
    generateRDGrossRent: {
      path: 'generateRDGrossRent',
      dataType: 'string',
    },
    uploadedDocument: {
      path: 'document.createdAt',
      dataType: 'date',
    },
  },
  [GROSS_RENT_CERT_TYPE]: {
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorPlanName',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    newGrossRent: {
      path: 'newGrossRent',
      dataType: 'number',
    },
    newHUDUA: {
      path: 'newHUDUA',
      dataType: 'number',
    },
    grEffectiveDate: {
      path: 'grEffectiveDate',
      dataType: 'date',
    },
    anticipatedVoucherDate: {
      path: 'anticipatedVoucherDate',
      dataType: 'date',
    },
    priorCertificationDate: {
      path: 'priorCertificationDate',
      dataType: 'date',
    },
    otherCertInProgress: {
      path: 'otherCertInProgress',
      dataType: 'string',
    },
    occupiedOnNTV: {
      path: 'occupiedOnNTV',
      dataType: 'string',
    },
    newSubsidy: {
      path: 'newSubsidy',
      dataType: 'number',
    },
    newResidentRent: {
      path: 'newResidentRent',
      dataType: 'number',
    },
    newUtilityReimbursement: {
      path: 'newUtilityReimbursement',
      dataType: 'number',
    },
    residentSignatureRequired: {
      path: 'residentSignatureRequired',
      dataType: 'string',
    },
    correction: {
      path: 'isCorrection',
      dataType: 'string',
    },
    generated59a: {
      path: 'generated59a',
      dataType: 'string',
    },
    uploadedDocument: {
      path: 'document.createdAt',
      dataType: 'date',
    },
  },
  [MOVE_IN_CERT_TYPE]: {
    status: {
      path: 'displayStatus',
      dataType: 'string',
    },
    programName: {
      path: 'programName',
      dataType: 'string',
    },
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorplan',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    moveInDate: {
      path: 'moveInDate',
      dataType: 'date',
    },
    leaseStartDate: {
      path: 'leaseStartDate',
      dataType: 'date',
    },
    isResident: {
      path: 'isResident',
      dataType: 'string',
    },
    balance: {
      path: 'balance',
      dataType: 'number',
    },
    isCorrection: {
      path: 'isCorrection',
      dataType: 'string',
    },
    complianceDocument: {
      path: 'latestDocument.documentType.name',
      dataType: 'string',
    },
    daysSinceLastUpload: {
      path: 'daysSinceLastUpload',
      dataType: 'number',
    },
    certificationNotes: {
      path: 'certificationNotes',
      dataType: 'string',
    },
  },
  [INITIAL_CERT_TYPE]: {
    status: {
      path: 'displayStatus',
      dataType: 'string',
    },
    programName: {
      path: 'programName',
      dataType: 'string',
    },
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorPlan',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    effectiveDate: {
      path: 'voucherEffectiveDate',
      dataType: 'date',
    },
    leaseStartDate: {
      path: 'leaseStartDate',
      dataType: 'date',
    },
    isPriorResident: {
      path: 'isPriorResident',
      dataType: 'string',
    },
    balance: {
      path: 'balance',
      dataType: 'number',
    },
    isCorrection: {
      path: 'isCorrection',
      dataType: 'string',
    },
    latestComplianceDocument: {
      path: 'latestDocument.documentType.name',
      dataType: 'string',
    },
    daysSinceLastUpload: {
      path: 'daysSinceLastUpload',
      dataType: 'number',
    },
    certificationNotes: {
      path: 'certificationNotes',
      dataType: 'string',
    },
  },
  // INTERIM = OTHER_LIHTC
  [INTERIM_CERT_TYPE]: {
    status: {
      path: 'displayStatus',
      dataType: 'string',
    },
    programName: {
      path: 'programName',
      dataType: 'string',
    },
    unit: {
      path: 'unit',
      dataType: 'string',
    },
    floorPlan: {
      path: 'floorplan',
      dataType: 'string',
    },
    headOfHousehold: {
      path: 'headOfHousehold',
      dataType: 'string',
    },
    effectiveDate: {
      path: 'voucherEffectiveDate',
      dataType: 'date',
    },
    lastCertifiedDate: {
      path: 'lastCertifiedDate',
      dataType: 'date',
    },
    nextRecertDueDate: {
      path: 'nextRecertDueDate',
      dataType: 'date',
    },
    isPriorResident: {
      path: 'isPriorResident',
      dataType: 'string',
    },
    isCorrection: {
      path: 'isCorrection',
      dataType: 'string',
    },
    balance: {
      path: 'balance',
      dataType: 'number',
    },
    latestComplianceDocument: {
      path: 'latestDocument.documentType.name',
      dataType: 'string',
    },
    daysSinceLastUpload: {
      path: 'daysSinceLastUpload',
      dataType: 'number',
    },
    certificationNotes: {
      path: 'certificationNotes',
      dataType: 'string',
    },
  },
  [OTHER_HUD]: OTHER_PROPERTY_PATH_MAP,
  [OTHER]: OTHER_PROPERTY_PATH_MAP,
};
